import React, { useEffect, useState } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import Dashboard from "./Dashboard";

const API_URL = process.env.REACT_APP_API_URL;

const fmtDateTime = (d) =>
  d
    ? new Date(d).toLocaleString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Asia/Kolkata",
      })
    : "NA";

// Expand a record into its legs (with per-leg accommodation), matching the tables.
const expandLegs = (t) => {
  const stays = Array.isArray(t.accommodations) ? t.accommodations : [];
  if (Array.isArray(t.travelLegs) && t.travelLegs.length) {
    return t.travelLegs.map((l, idx) => {
      const s = stays[idx] || null;
      return {
        fromLocation: l.fromLocation || "",
        toLocation: l.toLocation || "",
        travelDate: l.travelDate || null,
        accommodationPlace: l.accommodationPlace || (s && s.location) || "",
        accommodationStartDate: l.accommodationStartDate || (s && s.startDate) || null,
        accommodationEndDate: l.accommodationEndDate || (s && s.endDate) || null,
      };
    });
  }
  const s = stays[0] || null;
  return [
    {
      fromLocation: t.fromLocation || "",
      toLocation: t.toLocation || "",
      travelDate: t.travelDate || null,
      accommodationPlace: (s && s.location) || "",
      accommodationStartDate: (s && s.startDate) || t.accommodationStartDate || null,
      accommodationEndDate: (s && s.endDate) || t.accommodationEndDate || null,
    },
  ];
};

const TravelApprovals = () => {
  const [records, setRecords] = useState([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");

  const fetchQueue = async () => {
    try {
      const token = await localforage.getItem("token");
      const res = await api.get(`${API_URL}/travel/reviewer-queue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(Array.isArray(res.data.records) ? res.data.records : []);
    } catch (err) {
      setError("Failed to load your approvals. Please try again later.");
    }
  };

  useEffect(() => {
    fetchQueue();
  }, []);

  const decide = async (id, decision) => {
    if (
      decision === "rejected" &&
      !window.confirm("Reject this travel request? The requester will be notified in their status view.")
    )
      return;
    setBusyId(id);
    try {
      const token = await localforage.getItem("token");
      await api.put(
        `${API_URL}/travel/reviewer-decision/${id}`,
        { decision },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecords((prev) => prev.filter((r) => r._id !== id));
    } catch (err) {
      alert(err.response?.data?.error || "Could not submit your decision.");
    } finally {
      setBusyId("");
    }
  };

  return (
    <div>
      <Dashboard />
      <div style={{ padding: "20px", maxWidth: "1100px", margin: "0 auto" }}>
        <h2 style={{ textAlign: "center", marginBottom: "6px" }}>
          Travel Approvals
        </h2>
        <p style={{ textAlign: "center", color: "#6a7c93", marginTop: 0 }}>
          Requests from your team awaiting your review. Approve to send to admin;
          reject to stop the request.
        </p>

        {error && (
          <p style={{ color: "#b91c1c", textAlign: "center" }}>{error}</p>
        )}

        {records.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              color: "#6a7c93",
              padding: "40px",
              border: "1px dashed #cfd8e3",
              borderRadius: "12px",
            }}
          >
            No travel requests are awaiting your approval.
          </div>
        ) : (
          records.map((t) => {
            const legs = expandLegs(t);
            return (
              <div
                key={t._id}
                style={{
                  border: "1px solid #e3e9f1",
                  borderRadius: "12px",
                  padding: "16px",
                  marginBottom: "14px",
                  background: "#fff",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "8px" }}>
                  <div style={{ fontWeight: 700, fontSize: "16px", color: "#1b2b41" }}>
                    {t.name || t.email}{" "}
                    <span style={{ fontWeight: 400, color: "#6a7c93", fontSize: "14px" }}>
                      · {t.employeePhoneNumber || "no phone"} · {t.requestType || "—"}
                    </span>
                  </div>
                  <div style={{ color: "#6a7c93", fontSize: "13px" }}>
                    Travel Code: {t.travelCode || "—"}
                  </div>
                </div>

                <div style={{ margin: "8px 0", fontSize: "14px", color: "#334" }}>
                  <div><strong>Purpose:</strong> {t.purposeOfTravel || "—"}</div>
                  <div><strong>Event:</strong> {t.eventName || "—"} {t.eventLocation ? `· ${t.eventLocation}` : ""}</div>
                  {t.eventDetails ? <div><strong>Event Details:</strong> {t.eventDetails}</div> : null}
                  {t.remarks ? <div><strong>Remarks:</strong> {t.remarks}</div> : null}
                </div>

                <div style={{ borderTop: "1px dashed #e3e9f1", paddingTop: "8px", fontSize: "14px" }}>
                  <div style={{ fontWeight: 600, marginBottom: "4px" }}>Itinerary</div>
                  {legs.map((l, i) => (
                    <div key={i} style={{ marginBottom: "4px" }}>
                      {i + 1}.{" "}
                      {l.fromLocation || l.toLocation
                        ? `${l.fromLocation || "?"} → ${l.toLocation || "?"} · ${fmtDateTime(l.travelDate)}`
                        : "No travel"}
                      {l.accommodationPlace
                        ? ` | Stay: ${l.accommodationPlace} (${fmtDateTime(l.accommodationStartDate)} → ${fmtDateTime(l.accommodationEndDate)})`
                        : ""}
                    </div>
                  ))}
                </div>

                <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
                  <button
                    type="button"
                    disabled={busyId === t._id}
                    onClick={() => decide(t._id, "approved")}
                    style={{
                      background: "#166534", color: "#fff", border: "none",
                      borderRadius: "8px", padding: "8px 18px", fontWeight: 600,
                      cursor: busyId === t._id ? "not-allowed" : "pointer",
                    }}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={busyId === t._id}
                    onClick={() => decide(t._id, "rejected")}
                    style={{
                      background: "#b91c1c", color: "#fff", border: "none",
                      borderRadius: "8px", padding: "8px 18px", fontWeight: 600,
                      cursor: busyId === t._id ? "not-allowed" : "pointer",
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TravelApprovals;
